"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Sparkles,
  UserCircle,
  Wrench,
  Link2,
  BookOpen,
  Users,
  ArrowDown,
  ArrowRight,
  Hand,
  Blocks,
} from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DemoCTA, PrimaryCTA } from "~/components/siteglue/siteglue-cta";
import { NeverMissHeroHeadline } from "~/components/marketing/never-miss-hero-headline";
import {
  MARKETING_PRODUCT_PROFILE,
  MARKETING_PRODUCT_RESPONSIBILITIES,
  MARKETING_PRODUCT_TOOLS,
  MARKETING_PRODUCT_TRAINING,
  MARKETING_PRODUCT_INTEGRATIONS,
  scrollToInboundSection,
} from "~/lib/inbound-gpt/paths";
import { getMarketingChrome, SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { SITEGLUE_PAGE_STORY } from "~/lib/siteglue/page-story";

const inboundFeatureSections = [
  {
    id: "identity",
    label: "Agent Profile",
    description: "Persona, team members & locations",
    icon: UserCircle,
    href: MARKETING_PRODUCT_PROFILE,
  },
  {
    id: "responsibilities",
    label: "Agent Responsibilities",
    description: "Live chat, phone, text, email & more",
    icon: Wrench,
    href: MARKETING_PRODUCT_RESPONSIBILITIES,
  },
  {
    id: "tools",
    label: "Agent Tools",
    description: "Appointments, MCP servers & integrations",
    icon: Link2,
    href: MARKETING_PRODUCT_TOOLS,
  },
  {
    id: "training",
    label: "Agent Training",
    description: "Knowledge base, RAG sources & testing",
    icon: BookOpen,
    href: MARKETING_PRODUCT_TRAINING,
  },
  {
    id: "agents",
    label: "All Agents",
    description: "Browse healthcare AI templates",
    icon: Users,
    href: null as string | null,
  },
] as const;

const siteglueFeatureSections = [
  {
    id: "human-in-the-loop",
    label: "Human-in-the-Loop™",
    description: "Always a human in the loop",
    icon: Hand,
    href: null as string | null,
  },
  {
    id: "identity",
    label: "Agent Profile",
    description: "Persona, team & locations",
    icon: UserCircle,
    href: MARKETING_PRODUCT_PROFILE,
  },
  {
    id: "responsibilities",
    label: "Communication Channels",
    description: "Chat, phone, SMS, email & more",
    icon: Wrench,
    href: MARKETING_PRODUCT_RESPONSIBILITIES,
  },
  {
    id: "tools",
    label: "AI Skills",
    description: "Scheduling, handoff & tools",
    icon: Link2,
    href: MARKETING_PRODUCT_TOOLS,
  },
  {
    id: "training",
    label: "Knowledge & Training",
    description: "Knowledge base & RAG sources",
    icon: BookOpen,
    href: MARKETING_PRODUCT_TRAINING,
  },
  {
    id: "integrations",
    label: "Integrations",
    description: "Calendars, EMR & MCP workflows",
    icon: Blocks,
    href: MARKETING_PRODUCT_INTEGRATIONS,
  },
] as const;

function scrollToSection(sectionId: string) {
  scrollToInboundSection(sectionId);
  window.history.replaceState(null, "", `#${sectionId}`);
}

export function FeaturesHero() {
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";
  const featureSections = isSiteGlue ? siteglueFeatureSections : inboundFeatureSections;

  if (isSiteGlue) {
    return (
      <section
        data-testid="section-features-hero"
        className="relative overflow-hidden border-b border-slate-200/80 bg-white pt-20 pb-16 sm:pt-24 sm:pb-20"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute left-1/2 top-0 h-[520px] w-[820px] -translate-x-1/2 rounded-full opacity-70 blur-[120px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.blue}18, transparent 60%)`,
            }}
          />
          <div
            className="absolute bottom-0 right-0 h-[280px] w-[420px] rounded-full opacity-50 blur-[100px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.green}20, transparent 70%)`,
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#4f81bd]"
            >
              {SITEGLUE_PAGE_STORY.features.navLabel}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.02 }}
              className="mb-5 text-base font-medium text-slate-500"
            >
              {SITEGLUE_PAGE_STORY.features.line}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 }}
              className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl md:text-6xl"
            >
              Always a human in the loop.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="mt-6 text-xl font-medium leading-snug text-slate-700 sm:text-2xl"
            >
              AI that works alongside your team—not instead of it.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              SiteGlue automates conversations, captures leads, answers questions, and books
              appointments while keeping your team in complete control.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
            >
              <PrimaryCTA location="hero" testId="button-features-start-free" />
              <DemoCTA location="hero" testId="button-features-live-demo" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className="mx-auto mt-16 grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-3"
          >
            {featureSections.map((section) => {
              const Icon = section.icon;
              const className =
                "group rounded-2xl border border-slate-200/80 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#4f81bd]/30 hover:shadow-md";
              const content = (
                <>
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#4f81bd]/10 transition group-hover:bg-[#4f81bd]/15">
                    <Icon className="h-5 w-5 text-[#4f81bd]" />
                  </div>
                  <p className="font-semibold text-slate-900">{section.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">
                    {section.description}
                  </p>
                </>
              );

              if (section.href) {
                return (
                  <Link
                    key={section.id}
                    href={section.href}
                    className={className}
                    data-testid={`link-features-${section.id}`}
                  >
                    {content}
                  </Link>
                );
              }

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className={className}
                  data-testid={`link-features-${section.id}`}
                >
                  {content}
                </button>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="mt-12 flex justify-center"
          >
            <button
              type="button"
              onClick={() => scrollToSection("human-in-the-loop")}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#4f81bd]"
              aria-label="Scroll to How SiteGlue Works With Your Team"
            >
              Explore how it works with your team
              <ArrowDown className="h-4 w-4 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="section-features-hero"
      className="relative border-b border-border pt-20 pb-16 sm:pt-24 sm:pb-20"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-primary/6 blur-[120px]" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <Badge
              variant="secondary"
              className="mb-6 px-4 py-1.5 text-base"
              style={{ color: chrome.primaryColor }}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {chrome.productName} Platform
            </Badge>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
          >
            <NeverMissHeroHeadline />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl"
          >
            Generate a custom {chrome.productName} agent that works for your company.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15 }}
            className="mt-8 flex justify-center"
          >
            <Link href="/">
              <Button size="lg" className="gap-2 px-8 py-6 text-base" data-testid="button-features-get-started-free">
                Get started free
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mx-auto mt-14 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5"
        >
          {inboundFeatureSections.map((section) => {
            const Icon = section.icon;
            const className =
              "group rounded-xl border border-border bg-card/80 p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md";
            const content = (
              <>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 transition group-hover:bg-primary/15">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <p className="font-semibold text-foreground">{section.label}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {section.description}
                </p>
              </>
            );

            if (section.href) {
              return (
                <Link
                  key={section.id}
                  href={section.href}
                  className={className}
                  data-testid={`link-features-${section.id}`}
                >
                  {content}
                </Link>
              );
            }

            return (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollToSection(section.id)}
                className={className}
                data-testid={`link-features-${section.id}`}
              >
                {content}
              </button>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="mt-10 flex justify-center"
        >
          <button
            type="button"
            onClick={() => scrollToSection("identity")}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition hover:text-primary"
            aria-label={`Scroll to ${chrome.productName} Agent Profile`}
          >
            Explore features
            <ArrowDown className="h-4 w-4 animate-bounce" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
